import React from "react"
import { ChevronDown } from "lucide-react"
import { SectionHeading } from "./ui/SectionHeading"

interface FAQItem {
	question: string
	answer: React.ReactNode
}

/**
 * FAQ content. Keep in sync with the static FAQ section and FAQPage JSON-LD
 * in public/index.html (crawlers only see the static copy).
 */
const FAQ_ITEMS: FAQItem[] = [
	{
		question: "Jak probíhá spolupráce s vaší firmou?",
		answer: "Začínáme úvodní konzultací zdarma. Poté provedeme analýzu vašich procesů a systémů a přiřadíme jim rating podle optimalizačního potenciálu. Na jejím základě navrhneme cílovou architekturu a integrační scénář, realizaci dodáme po iteracích a u kritických operací držíme plný odborný dohled až do ostrého provozu."
	},
	{
		question: "Kolik spolupráce stojí?",
		answer: "Úvodní konzultace je zdarma. Analýza a rating procesů začíná na 25 000 Kč, vývoj integrací a adaptérů účtujeme od 1 500 Kč za hodinu nebo fixní cenou za projekt, odborný dohled a podporu paušálem od 8 000 Kč měsíčně. Nejsme plátci DPH, ceny jsou konečné."
	},
	{
		question: "Jak rychle odpovíte na poptávku?",
		answer: "Na e-mail, telefon i WhatsApp odpovídáme do 24 hodin v pracovní dny. Telefonicky jsme k dispozici v pondělí až pátek od 9:00 do 17:00. Zákazníkům se sjednaným SLA reagujeme na kritický incident do 4 hodin."
	},
	{
		question: "Pro jaké firmy jsou vaše služby vhodné?",
		answer: "Pro malé a střední firmy a technologické týmy, které mají data rozesetá v několika nástrojích, ručně přepisují informace mezi systémy nebo chtějí zavést AI, ale potřebují odborný dohled nad bezpečnou realizací změn. Typicky výrobní firmy, B2B platformy a rostoucí online byznysy."
	},
	{
		question: "Můžeme začít malým projektem?",
		answer: "Ano. Doporučujeme začít analýzou nebo jednou konkrétní integrací (například propojení CRM s e-shopem nebo účetnictvím). Výsledek uvidíte během několika týdnů a podle něj se rozhodnete o dalších krocích."
	},
	{
		question: "Pracujete i vzdáleně nebo mimo Prahu?",
		answer: "Ano. Většinu práce děláme vzdáleně, schůzky vedeme online nebo u vás na místě v Praze a okolí. Sídlíme na adrese Legerova 1815/33, Praha 2."
	},
	{
		question: "Poskytujete dohled i po nasazení řešení do provozu?",
		answer: "Ano. Řídíme release management a nasazení bez výpadků, nastavujeme aplikační monitoring a logování a zajišťujeme expertní supervizi i po spuštění řešení do ostrého provozu."
	},
	{
		question: "Jaké technologie a AI modely využíváte?",
		answer: "Podle povahy úlohy nasazujeme lokální modely (např. LLaMA), rychlá API (Groq) i standardní modely jako Claude, Gemini nebo ChatGPT. Integrace stavíme na platformě Make, vlastních adaptérech, API a event-driven architektuře."
	},
	{
		question: "Jak dlouho trvá analýza a implementace?",
		answer: "Analýza a rating procesů trvá obvykle 1 až 3 týdny. Délka realizace závisí na počtu a složitosti propojovaných systémů; menší integrace dodáváme v řádu týdnů, větší projekty dělíme na milníky s průběžnými ukázkami."
	},
	{
		question: "Nabízíte záruku na dodaná řešení?",
		answer: (
			<>
				Ano. Na dodaná řešení poskytujeme záruku 6 měsíců od předání do provozu a na kritický
				incident reagujeme do 4 hodin v pracovní dny. Podrobnosti popisuje stránka{" "}
				<a href="/zaruka.html">Záruka a SLA</a>.
			</>
		)
	}
]

/**
 * FAQ Component — native <details> accordion: works without JS, the full
 * content stays in the DOM (matches the static copy in public/index.html)
 * and the first question is open by default.
 */
export const FAQ: React.FC = () => {
	return (
		<section id="faq" className="lp-section">
			<div className="lp-container">
				<SectionHeading
					eyebrow="FAQ"
					title="Časté dotazy"
					lead="Odpovědi na otázky, které od firem slýcháme nejčastěji. Nenašli jste tu svoji? Napište nám."
				/>

				<div className="lp-faq">
					{FAQ_ITEMS.map((item, idx) => (
						<details
							key={item.question}
							className="lp-faq__item"
							open={idx === 0}
							data-reveal=""
							style={{ "--reveal-delay": `${Math.min(idx, 6) * 50}ms` } as React.CSSProperties}
						>
							<summary className="lp-faq__q">
								<span>{item.question}</span>
								<span className="lp-faq__icon" aria-hidden="true">
									<ChevronDown size={18} strokeWidth={2.5} />
								</span>
							</summary>
							<div className="lp-faq__a">
								<p>{item.answer}</p>
							</div>
						</details>
					))}
				</div>
			</div>
		</section>
	)
}
